import { Flex, Spinner, cn } from '@kognitos/lattice';
import { showScrollbarWhileScrolling } from '@/shared/utils';

import type {
  EOutputSourceView,
  IMetadataItem,
  TResolvedValue,
} from '../../types';
import type { TOutputModalLayout } from '../output-presentation';
import { MetadataSidebar } from './MetadataSidebar';
import { PreviewContent } from './PreviewContent';

interface IDefaultLayoutProps {
  value: TResolvedValue | null;
  outputName: string;
  metadataItems: IMetadataItem[];
  selectedIndex: number;
  onSelect: (index: number) => void;
  onClose: () => void;
  onDownload?: (value: TResolvedValue, outputName: string) => void;
  layout: TOutputModalLayout;
  /**
   * Swaps the content area for a spinner while the value is fetched, keeping
   * the frame and header in place. Rendering a separate loading tree instead
   * made the dialog visibly reflow the moment bytes arrived.
   */
  isLoading?: boolean;
  sourceView?: EOutputSourceView;
  /** Raw text for `sourceView`, resolved by the caller so it is fetched/decoded once. */
  sourceText?: string | null;
}

/**
 * Body of the preview modal for non-browser outputs: the metadata sidebar when
 * there is more than one item, and the preview itself. The title and the
 * copy/download/close actions live in `OutputModalHeader`, above this.
 */
export function DefaultLayout({
  value,
  outputName,
  metadataItems,
  selectedIndex,
  onSelect,
  onClose,
  onDownload,
  layout,
  isLoading = false,
  sourceView,
  sourceText = null,
}: IDefaultLayoutProps) {
  // Nothing to page between while loading, and showing the sidebar then
  // swapping it is the reflow `isLoading` exists to avoid.
  const hasSidebar = !isLoading && metadataItems.length > 1;

  return (
    // lattice Flex defaults to align="start"; stretch keeps the sidebar and
    // preview at full height.
    <Flex
      data-testid="output-modal-body"
      position="relative"
      align="stretch"
      flex="1"
      // Only the sidebar (file-list) case gets a vertical inset; left and
      // right stay flush so the sidebar lines up with the title.
      gap={hasSidebar ? 4 : undefined}
      padding={hasSidebar ? 4 : undefined}
      overflow="hidden"
      onScrollCapture={showScrollbarWhileScrolling}
      className={cn(
        'scrollbars-on-scroll min-h-0',
        `output-modal-body--${layout}`,
        hasSidebar && 'output-modal-body--with-sidebar',
      )}
    >
      {hasSidebar && (
        <Flex
          direction="column"
          align="stretch"
          height="full"
          // `shrink-0` (not `min-w-*`) is what stops the preview column from squeezing this; the class itself is just the mobile-breakpoint hook now.
          className="output-modal-metadata-sidebar w-72 shrink-0 min-h-0"
        >
          <MetadataSidebar
            items={metadataItems}
            selectedIndex={selectedIndex}
            onSelect={onSelect}
          />
        </Flex>
      )}

      {/* The ONE scroll container for the content — the renderers inside are
          told to fill it rather than scroll themselves, which is what produced
          a second scrollbar. */}
      <Flex
        data-testid="output-modal-preview"
        direction="column"
        align="stretch"
        flex="1"
        overflow="auto"
        className="min-h-0 min-w-0"
      >
        {isLoading ? (
          <Flex
            align="center"
            justify="center"
            width="full"
            height="full"
            padding={4}
            className="min-h-0"
          >
            <Spinner />
          </Flex>
        ) : (
          <PreviewContent
            value={value}
            onClose={onClose}
            outputName={outputName}
            onDownload={onDownload}
            layout={layout}
            sourceView={sourceView}
            sourceText={sourceText}
          />
        )}
      </Flex>
    </Flex>
  );
}
